import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Drawer, Input, Row } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import OnTraccrButton from '../../../common/buttons/OnTraccrButton';
import DrawerFooter from '../../../common/containers/DrawerFooter';
import StepsContainer from '../../../common/containers/StepsContainer';

import colors from '../../../constants/Colors';

import { includesTerm } from '../../../helpers/helpers';

import useProjectGlobalCostcodes from '../../../costcodes/ProjectGlobalCostcodes';

import ConfigureWageRatesTable from './ConfigureWageRatesTable';
import CostCodeSelectionTable from './CostCodeSelectionTable';

export default function ProjectAddGlobalCostCodeDrawer({
  divisionId,
  visible,
  onClose,
  onSubmit,
  chosen = [],
}) {
  const { t } = useTranslation();
  const { globalCostcodes: costcodes } = useProjectGlobalCostcodes(visible);

  const [currentStep, setCurrentStep] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [wageConfigurations, setWageConfigurations] = useState({});
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Reset state when drawer opens/closes
  useEffect(() => {
    if (visible) {
      setCurrentStep(0);
      setSelectedRowKeys([]);
      setSearchText('');
      setWageConfigurations({});
      setExpandedRowKeys([]);
    }
  }, [visible]);

  // Filter available costcodes
  const availableCostcodes = useMemo(() => {
    const chosenSet = new Set(chosen.map(({ id }) => id));
    const chosenCodeSet = new Set(chosen.map(({ code }) => code ? code.trim().toLowerCase() : code));
    const chosenNameSet = new Set(chosen.map(({ name }) => name ? name.trim().toLowerCase() : name));
    return costcodes.filter((costcode) => (
      costcode.active
      && !costcode.projectId
      && costcode.divisionId === divisionId
      && !chosenSet.has(costcode.id)
      && !chosenCodeSet.has(costcode.code ? costcode.code.trim().toLowerCase() : costcode.code)
      && !chosenNameSet.has(costcode.name ? costcode.name.trim().toLowerCase() : costcode.name)
      && (!searchText
        || includesTerm(costcode.name, searchText)
        || includesTerm(costcode.code, searchText)
        || includesTerm(costcode.description, searchText)
      )
    ));
  }, [costcodes, divisionId, chosen, searchText]);

  // Get selected costcodes for step 2
  const selectedCostcodes = useMemo(() => {
    const selectedSet = new Set(selectedRowKeys);
    return costcodes.filter((cc) => selectedSet.has(cc.id));
  }, [costcodes, selectedRowKeys]);

  const onSearchChanged = useCallback((e) => {
    const { target: { value } = {} } = e;
    setSearchText(value || '');
  }, []);

  const onSelectionChange = useCallback((newSelectedKeys) => {
    // keep filtered-out selections + new selections from visible items
    const availableIds = new Set(availableCostcodes.map((cc) => cc.id));
    const filteredOutSelections = selectedRowKeys.filter((id) => !availableIds.has(id));
    setSelectedRowKeys([...filteredOutSelections, ...newSelectedKeys]);
  }, [availableCostcodes, selectedRowKeys]);

  const onWageChange = useCallback((costcodeId, field, value) => {
    setWageConfigurations((prev) => ({
      ...prev,
      [costcodeId]: {
        ...prev[costcodeId],
        [field]: value,
      },
    }));
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep !== 0 || selectedRowKeys.length === 0) return;

    // Pre-fill wage configurations from selected cost codes
    const prefilled = {};
    selectedCostcodes.forEach((cc) => {
      prefilled[cc.id] = {
        hourlyWage: cc.hourlyWage,
        dailyWage: cc.dailyWage,
        wage: cc.wage,
        wageAdjustment: cc.wageAdjustment,
        wageMultiplier: cc.wageMultiplier,
        hourlyBillingRate: cc.hourlyBillingRate,
        dailyBillingRate: cc.dailyBillingRate,
      };
    });
    setWageConfigurations(prefilled);
    setCurrentStep(1);
    setExpandedRowKeys([]); // Start with all rows collapsed
  }, [currentStep, selectedRowKeys, selectedCostcodes]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleSubmit = useCallback(() => {
    const result = selectedCostcodes.map((cc) => {
      const { categoryIds = [] } = cc;

      const defaultCategoryAssignments = {};

      categoryIds.forEach((categoryId) => {
        const isCustomCategory = typeof categoryId === 'string' && categoryId.length > 1;
        defaultCategoryAssignments[categoryId] = {
          hours: 0,
          days: 0,
          estimatedCost: 0,
          defaultCategoryId: isCustomCategory ? null : categoryId,
          customCategoryId: isCustomCategory ? categoryId : null,
        };
      });

      return {
        ...cc,
        ...wageConfigurations[cc.id], // Merge wage configurations into selected costcodes
        categoryAssignments: defaultCategoryAssignments,
      };
    });
    onSubmit(result);
  }, [selectedCostcodes, wageConfigurations, onSubmit]);

  const steps = useMemo(() => [
    {
      title: 'Select Cost Codes',
      content: (
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Input.Search
              type="search"
              placeholder="Search cost codes"
              allowClear
              style={{
                flex: 1,
                height: 40,
                borderRadius: 8,
                borderColor: colors.GRAY_400,
              }}
              onChange={onSearchChanged}
              value={searchText}
            />
          </div>
          <CostCodeSelectionTable
            dataSource={availableCostcodes}
            selectedRowKeys={selectedRowKeys}
            onSelectionChange={onSelectionChange}
            emptyText="No cost codes available"
            scrollY={400}
          />
          <div style={{ marginTop: 8, color: colors.GRAY_800, fontSize: 13 }}>
            {selectedRowKeys.length} cost code(s) selected
          </div>
        </div>
      ),
    },
    {
      title: 'Configure Wage Rates',
      content: (
        <div>
          <div style={{ marginBottom: 16, color: colors.GRAY_800 }}>
            Expand each row to configure wage rates for the selected cost codes.
          </div>
          <ConfigureWageRatesTable
            dataSource={selectedCostcodes}
            wageConfigurations={wageConfigurations}
            onWageChange={onWageChange}
            expandedRowKeys={expandedRowKeys}
            onExpandedRowsChange={setExpandedRowKeys}
          />
        </div>
      ),
    },
  ], [
    searchText,
    onSearchChanged,
    availableCostcodes,
    selectedRowKeys,
    onSelectionChange,
    selectedCostcodes,
    wageConfigurations,
    onWageChange,
    expandedRowKeys,
    setExpandedRowKeys,
  ]);

  return (
    <Drawer
      title={`Add Global Cost Codes to ${t('Project')}`}
      visible={visible}
      onClose={onClose}
      width={700}
      bodyStyle={{ paddingBottom: 80 }}
    >
      <StepsContainer
        currentStep={currentStep}
        steps={steps}
      />
      <DrawerFooter style={{ padding: '16px 24px', borderColor: colors.GRAY_200 }}>
        <Row justify="space-between">
          <div>
            {currentStep > 0 && (
              <OnTraccrButton
                title="Back"
                type="cancel"
                onClick={handleBack}
              />
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <OnTraccrButton
              title="Cancel"
              type="cancel"
              onClick={onClose}
            />
            {currentStep === 0 ? (
              <OnTraccrButton
                title="Next"
                onClick={handleNext}
                disabled={selectedRowKeys.length === 0}
              />
            ) : (
              <OnTraccrButton
                title="Submit"
                onClick={handleSubmit}
              />
            )}
          </div>
        </Row>
      </DrawerFooter>
    </Drawer>
  );
}

/* eslint-disable react/forbid-prop-types */
ProjectAddGlobalCostCodeDrawer.propTypes = {
  divisionId: PropTypes.string,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  chosen: PropTypes.array,
};

ProjectAddGlobalCostCodeDrawer.defaultProps = {
  divisionId: null,
  visible: false,
  onClose: () => {},
  onSubmit: () => {},
  chosen: [],
};
